import React, { useRef, useState } from 'react';
import { cardLayer1 } from '../lib/layerClasses';
import { badgeBaseClass } from './Badge';
import { AnchoredPanel } from './Popover';
import { type Tone, toneClasses } from '../lib/tones';
import { useIsMobile } from '../contexts/ViewportContext';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * StatCardGrid — the ONE way to render a row of KPI cards in either app.
 *
 * The grid is owned here, never written by the page: columns are derived from
 * `cards.length`, and a trailing lone card always spans its row. That rule
 * replaces the fifteen hand-written `grid grid-cols-…` strings and the three
 * copy-pasted `sm:col-span-2 lg:col-span-1` orphan fixes that preceded it.
 *
 * Mobile behaviour is owned by the component (via `useIsMobile`) and is NOT
 * configurable — every escape hatch here is how the codebase ended up with six
 * divergent stat cards in the first place:
 *   - the icon is hidden (decoration; the label carries the meaning),
 *   - density drops to `compact`,
 *   - a card with `detail` gets a tap affordance, because native `title`
 *     tooltips never fire on touch.
 *
 * The single card is deliberately NOT exported — exposing it invites a
 * hand-rolled grid, which is the divergence this component exists to remove.
 *
 * See documentation/Standards.md §20.
 */

/**
 * Six semantic tones plus `purple`, which is not a semantic but a MATERIAL
 * IDENTITY colour: CLAUDE.md fixes filament = amber and resin = purple across
 * the whole app. Filament's amber is already `warning`; resin had no
 * equivalent, and turning the resin card blue would break that convention on
 * the one screen where the two materials sit side by side.
 */
/** Alias of the platform-wide {@link Tone} vocabulary — kept as a named export
 *  because call sites annotate their card arrays with it. */
export type StatCardTone = Tone;

export interface StatCardConfig {
    /** Small uppercase label above the value. */
    label: string;
    /**
     * The metric itself — pass it already formatted (money, percentages…).
     * A node is allowed so a unit can be de-emphasised next to the figure,
     * e.g. `<>1.25 <span className="text-base">kg</span></>`.
     */
    value: React.ReactNode;
    /** Material Symbols ligature. Rendered on desktop only. */
    icon: IconName;
    /** Semantic colour of icon, value and pill. Defaults to `neutral`. */
    tone?: StatCardTone;
    /** Optional pill under the value. */
    sub?: string;
    /**
     * A second, smaller figure on the right of the value, on the SAME baseline,
     * with its label on the right of the `sub` row — so both figures and both
     * captions line up («25,62 kg · ≈ 813 g» over «31 aproximadas · en fallos»,
     * the print history). `tone` colours figure and label; default `danger`.
     */
    aside?: { value: React.ReactNode; label: string; tone?: StatCardTone };
    /**
     * How the figure moved against a comparison period, under the value: an
     * arrow and `label` («18 %»), then an optional `caption` («frente a 2.100 €»).
     * `good` colours it: `true` green, `false` red, `undefined` neutral — the
     * caller decides, because an expense going UP is bad and income going up
     * is good. Arrow + text, never colour alone.
     */
    trend?: StatCardTrend;
    /**
     * A footnote at the foot of the card, separated by a rule: what the figure
     * counts and what it leaves out («17 pedidos · sin impuestos ni envío»).
     */
    note?: string;
    /**
     * Breakdown shown on demand. Desktop: floats below the card on hover.
     * Mobile: expands inline from an ⓘ button.
     *
     * A node is allowed so a breakdown can carry swatches, rows and units —
     * a native `title` tooltip could render none of that, and never fired on
     * touch at all.
     */
    detail?: React.ReactNode;
    /** Makes the card actionable (filter, drill-down, navigation). */
    onClick?: () => void;
    /**
     * Active state for cards that act as FILTERS — the ring is what tells the
     * user which card produced the detail view rendered below it. Only
     * meaningful together with `onClick`.
     */
    selected?: boolean;
    /** DOM id — onboarding tours anchor on these. */
    id?: string;
}

export interface StatCardTrend {
    direction: 'up' | 'down' | 'flat';
    label: string;
    caption?: string;
    good?: boolean;
}

const TREND_ARROW: Record<StatCardTrend['direction'], string> = { up: '▲', down: '▼', flat: '=' };

interface StatCardGridProps {
    cards: StatCardConfig[];
    /** Denser cards for stacked desktop rows. Mobile always renders compact. */
    density?: 'normal' | 'compact';
    /** DOM id for the grid wrapper — onboarding tours anchor on these. */
    id?: string;
}


/* Tailwind scans source text, so every class below is a whole literal string —
 * never interpolate a column number into one or the JIT will not emit it. */
const DESKTOP_COLS: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
};
const DESKTOP_COLS_MANY = 'grid-cols-2 lg:grid-cols-4';
const MOBILE_COLS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2' };
const SPAN: Record<number, string> = { 2: 'col-span-2', 3: 'col-span-3' };
const LG_SPAN: Record<number, string> = { 2: 'lg:col-span-2', 3: 'lg:col-span-3', 4: 'lg:col-span-4' };

/** How many columns each breakpoint shows for `n` cards. */
const desktopMdCols = (n: number) => (n === 1 ? 1 : 2);
const desktopLgCols = (n: number) => Math.min(Math.max(n, 1), 4);
// Three cards on a phone are 2 + 1 (the third spans its row, `isOrphan`), not
// three across: a third of 360 px leaves ~45 px of label beside the ⓘ, and
// «ABGESCHLOSSEN (MONAT)», «UNVOLLSTÄNDIG» or «INCOMPLETOS» could only be
// chopped mid-word (2026-10-25).
const mobileCols = (n: number) => (n === 1 ? 1 : 2);

/** A lone card on the last row spans it, so it never sits orphaned in a corner. */
const isOrphan = (n: number, idx: number, cols: number) => idx === n - 1 && n > 1 && n % cols === 1;

const StatCardItem: React.FC<{
    card: StatCardConfig;
    compact: boolean;
    isMobile: boolean;
    spanClass: string;
}> = ({ card, compact, isMobile, spanClass }) => {
    const [detailOpen, setDetailOpen] = useState(false);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const tone = toneClasses[card.tone ?? 'neutral'];
    const aside = toneClasses[card.aside?.tone ?? 'danger'];

    const clickable = !!card.onClick;
    const hasDetail = card.detail !== undefined && card.detail !== null && card.detail !== '';
    // Two affordances for the same content, because hover does not exist on
    // touch: desktop reveals a floating panel on hover, mobile expands it
    // inline from an ⓘ button. The ⓘ is a separate control so it can coexist
    // with a card-level `onClick` (several cards both filter AND explain).
    const showDetailToggle = isMobile && hasDetail;
    const detailBody =
        typeof card.detail === 'string' ? (
            <div className="whitespace-pre-line text-[11px] font-medium leading-snug text-slate-500 dark:text-slate-400">
                {card.detail}
            </div>
        ) : (
            // A caller's own markup gets a readable default colour: the panel
            // sets none, so text without a colour class came out near-black on
            // the dark panel (the printer names of «Impresoras», 2026-10-18).
            <div className="text-text-main dark:text-slate-200">{card.detail}</div>
        );

    // `min-w-0` on the card + `truncate` on the value are one fix, not two: a
    // grid item defaults to `min-width:auto`, so without the first the 4xl value
    // widens its own track and pushes the card past the grid, and without the
    // second it just overflows the (now bounded) card instead.
    return (
        <div
            ref={cardRef}
            id={card.id}
            onClick={card.onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              card.onClick!();
                          }
                      }
                    : undefined
            }
            onMouseEnter={hasDetail && !isMobile ? () => setDetailOpen(true) : undefined}
            onMouseLeave={hasDetail && !isMobile ? () => setDetailOpen(false) : undefined}
            className={`${cardLayer1} min-w-0 ${compact ? 'p-3.5' : 'p-5'} ${spanClass} ${
                clickable
                    ? `cursor-pointer transition-all ${tone.hover} hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/30`
                    : ''
            } ${card.selected ? tone.ring : ''}`}
        >
            {/* The label may take two lines: three cards side by side on a
                phone leave ~80 px, and a German or Spanish word in wide-tracked
                capitals («ABGESCHLOSSEN (MONAT)», «UNVOLLSTÄNDIG», «INCOMPLETOS»)
                overflowed the card or ran under the ⓘ. On a phone the tracking
                drops to normal and a long word breaks (hyphenated where the
                browser knows the language) instead of spilling out. */}
            <div
                className={`flex items-start justify-between gap-1.5 ${
                    compact ? 'mb-1.5' : 'mb-3'
                } text-[10px] font-bold text-slate-400 uppercase ${isMobile ? 'tracking-normal' : 'tracking-wider'} leading-tight`}
            >
                <span className="min-w-0 break-words hyphens-auto">{card.label}</span>
                {showDetailToggle ? (
                    <button
                        type="button"
                        aria-expanded={detailOpen}
                        onClick={(e) => {
                            e.stopPropagation();
                            setDetailOpen((v) => !v);
                        }}
                        // Negative margin only up and right: its tap area grows into
                        // the card's padding, never over the label beside it.
                        className={`shrink-0 -my-1 -mr-1 p-1 ${tone.icon}`}
                    >
                        <Icon name="info" className="!text-[16px]" />
                    </button>
                ) : null}
                {!isMobile ? (
                    <Icon name={card.icon} className={`!text-[18px] shrink-0 ${tone.icon}`} />
                ) : null}
            </div>
            <div className={`flex items-baseline justify-between gap-2 ${hasDetail && !isMobile ? 'cursor-help' : ''}`}>
                <div
                    title={typeof card.value === 'string' ? card.value : undefined}
                    className={`min-w-0 truncate ${compact ? 'text-2xl' : 'text-4xl'} font-bold tabular-nums ${tone.value}`}
                >
                    {card.value}
                </div>
                {card.aside ? (
                    <div className={`shrink-0 ${compact ? 'text-base' : 'text-xl'} font-bold tabular-nums ${aside.value}`}>
                        {card.aside.value}
                    </div>
                ) : null}
            </div>
            {/* The sub pill IS a badge, so it borrows `badgeBaseClass` rather than
                re-declaring the same radius/padding/type scale — a shared component
                hand-rolling another shared component's geometry is exactly how the
                two drift apart. Only the compact variant stays bespoke: it drops the
                fill, border and padding entirely, so it is a different object, not a
                smaller badge. */}
            {card.sub || card.aside ? (
                compact || card.aside ? (
                    /* With an `aside` the sub is plain text even in `normal`
                       density: a pill on the left and a caption on the right
                       would not share a line. */
                    <div className="mt-1 flex items-baseline justify-between gap-2 text-[10px] font-bold uppercase tracking-wider">
                        <span className={`min-w-0 truncate ${tone.pillText}`}>{card.sub}</span>
                        {card.aside ? <span className={`shrink-0 ${aside.value}`}>{card.aside.label}</span> : null}
                    </div>
                ) : (
                    <div
                        title={card.sub}
                        className={`mt-2 ${badgeBaseClass} ${tone.pillBg} ${tone.pillText} ${tone.pillBorder}`}
                    >
                        {/* Its own span: the «…» of a long label (see `badgeBaseClass`). */}
                        <span className="min-w-0 truncate">{card.sub}</span>
                    </div>
                )
            ) : null}
            {card.trend ? (
                <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-[11px] leading-snug">
                    <span
                        className={`font-bold tabular-nums ${
                            card.trend.good === true
                                ? 'text-emerald-700 dark:text-emerald-400'
                                : card.trend.good === false
                                  ? 'text-red-700 dark:text-red-400'
                                  : 'text-slate-600 dark:text-slate-300'
                        }`}
                    >
                        <span aria-hidden>{TREND_ARROW[card.trend.direction]} </span>
                        {card.trend.label}
                    </span>
                    {card.trend.caption ? (
                        <span className="font-medium text-slate-500 dark:text-slate-400">{card.trend.caption}</span>
                    ) : null}
                </div>
            ) : null}
            {card.note ? (
                <div
                    className={`text-[11px] font-medium leading-snug text-slate-500 dark:text-slate-400 ${
                        compact ? 'mt-1.5' : 'mt-3 pt-2.5 border-t border-border-subtle dark:border-border-subtle-dark'
                    }`}
                >
                    {card.note}
                </div>
            ) : null}
            {/* Mobile: expands inline so it can never be clipped or fall off a
                narrow viewport. Desktop: floats below the card on hover. */}
            {hasDetail && detailOpen && isMobile ? (
                <div className="mt-2 pt-2 border-t border-border-subtle dark:border-border-subtle-dark">{detailBody}</div>
            ) : null}
            {/* Portalled, so a card inside an `overflow-hidden` container no
                longer clips its own breakdown — the panel used to be `absolute`
                and depended on no ancestor ever hiding overflow. */}
            <AnchoredPanel
                anchorRef={cardRef}
                open={hasDetail && detailOpen && !isMobile}
                width={260}
                label={typeof card.label === 'string' ? card.label : undefined}
            >
                {detailBody}
            </AnchoredPanel>
        </div>
    );
};

const StatCardGrid: React.FC<StatCardGridProps> = ({ cards, density = 'normal', id }) => {
    const isMobile = useIsMobile();
    const n = cards.length;
    if (n === 0) return null;

    const compact = density === 'compact' || isMobile;

    // Mobile is one JS-chosen column count; desktop keeps two CSS breakpoints
    // (md 768-1023 is always 2-up, lg 1024+ opens to the full count).
    const mCols = mobileCols(n);
    const mdCols = desktopMdCols(n);
    const lgCols = desktopLgCols(n);
    const gridCols = isMobile ? MOBILE_COLS[mCols] ?? 'grid-cols-2' : DESKTOP_COLS[n] ?? DESKTOP_COLS_MANY;

    return (
        <div id={id} className={`grid ${gridCols} gap-4`}>
            {cards.map((card, idx) => {
                const spanClass = isMobile
                    ? isOrphan(n, idx, mCols)
                        ? SPAN[mCols] ?? ''
                        : ''
                    : [
                          isOrphan(n, idx, mdCols) ? SPAN[mdCols] ?? '' : '',
                          isOrphan(n, idx, lgCols) ? LG_SPAN[lgCols] ?? '' : 'lg:col-span-1',
                      ]
                          .filter(Boolean)
                          .join(' ');
                return (
                    <StatCardItem
                        key={card.id ?? `${card.label}-${idx}`}
                        card={card}
                        compact={compact}
                        isMobile={isMobile}
                        spanClass={spanClass}
                    />
                );
            })}
        </div>
    );
};

export default StatCardGrid;
